import React, { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import {
  Button,
  EmptyStateProps,
  Modal,
  Pagination,
  failedEmptyState,
  searchEmptyState,
  useLoadPhase,
} from "../../../components/ui";
// Only for the first-run state below — see `EmptyState`'s `welcome` variant.
import { ReactComponent as FirstPlanMark } from "../../../assets/svg/No data images/plans.svg";
import { usePlanOptions } from "../hooks/usePlanOptions";
import { usePlanSave } from "../hooks/usePlanSave";
import { usePlans } from "../hooks/usePlans";
import { BrandPlan, NewBrandPlan } from "../types/brand.types";
import PlanFormModal from "./PlanFormModal";
import PlansTable from "./PlansTable";

const PLANS_PER_PAGE = 10;

/**
 * Plans the brand offers, listed the same way My clients lists clients:
 * one card, a sticky-header table that scrolls on its own, and a pinned
 * pagination footer.
 */
const BrandPlansTab: React.FC = () => {
  const { plans, status, reload, setPlans } = usePlans();

  // A list that lands quickly goes straight from blank to filled: the table's
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(status === "loading");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // The form dialog is open when `editing` is set: `null` creates, a plan
  // edits. Keeping one flag means the two states can't both be live.
  const [editing, setEditing] = useState<BrandPlan | null | undefined>(
    undefined
  );
  // The plan whose listing is being confirmed. Which way it is going is
  // read off its current status rather than stored: one flag cannot disagree
  // with the row it came from.
  const [pendingToggle, setPendingToggle] = useState<BrandPlan | null>(null);

  // Fetched here rather than inside the dialog, and once when the tab opens:
  // the dialog is mounted for the whole life of the tab, so loading them there
  // would tie a request to nothing in particular, and reloading them on each
  // open would put two on the wire every time someone pressed Add plan.
  const { suggestions, durations } = usePlanOptions();
  const { saving, create, update, setActive } = usePlanSave();

  // Filtering and paging are local because the list endpoint takes neither a
  // search term nor a sort, so `usePlans` fetches the lot. The day it takes
  // either, this memo becomes the fetch and `page`/`search` become its params.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return plans;
    return plans.filter(
      (plan) =>
        plan.name.toLowerCase().includes(query) ||
        plan.duration.toLowerCase().includes(query)
    );
  }, [plans, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / PLANS_PER_PAGE)
  );
  // Clamp rather than reset: a filter that shrinks the list past the current
  // page should land on the last page, not throw away the user's position.
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice(
    (currentPage - 1) * PLANS_PER_PAGE,
    currentPage * PLANS_PER_PAGE
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  /** What the table shows in place of rows, whichever way it came to have none.
   *  Nothing about loading: while the list is out the table shimmers a page of
   *  rows, and the status line below is what says so in words. */
  const emptyState = (): EmptyStateProps => {
    if (status === "failed")
      return failedEmptyState(
        "We could not load your plans",
        <Button size="sm" variant="secondary" onClick={reload}>
          Try again
        </Button>
      );

    if (search.trim()) return searchEmptyState("plans");

    // A brand with nothing on offer yet, which is where every workspace starts:
    // the first-run welcome rather than a list reporting a result.
    return {
      variant: "welcome",
      illustration: <FirstPlanMark aria-hidden className="w-11 h-11" />,
      title: "Add the plans you offer",
      description: "Your plans will show up once you create them.",
      highlights: {
        title: "With plans, you can",
        items: [
          "Set clear pricing for every plan you offer.",
          "Define how long each plan runs so clients know what to expect.",
        ],
      },
      action: (
        <Button
          size="md"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={() => setEditing(null)}
        >
          Create plan
        </Button>
      ),
    };
  };

  /**
   * Creates or saves, and takes the row from the response rather than from the
   * draft — the backend sets the id, the created date and the status, and on
   * an edit it is also what says which of the sent changes it kept.
   *
   * The dialog stays open on a failure, which the hook has already reported:
   * closing it would throw away a filled-in form over a request that can be
   * retried by pressing the same button again.
   */
  const handleSubmit = async (draft: NewBrandPlan) => {
    if (editing) {
      const saved = await update(editing, draft);
      if (!saved) return;

      setPlans((current) =>
        current.map((plan) => (plan.id === saved.id ? saved : plan))
      );
    } else {
      const saved = await create(draft);
      if (!saved) return;

      setPlans((current) => [saved, ...current]);
      // A live search or a later page would hide the row just added.
      setSearch("");
      setPage(1);
    }
    setEditing(undefined);
  };

  /**
   * Confirmed, so the listing is changed at the server and the row rebuilt
   * from what it answers with — the pill follows the backend's `is_active`
   * rather than the button that was pressed.
   *
   * The confirmation stays up on a failure, which the hook has already
   * reported, so the same button is the retry.
   */
  const handleConfirmToggle = async () => {
    if (!pendingToggle) return;

    const saved = await setActive(
      pendingToggle,
      pendingToggle.status !== "Active"
    );
    if (!saved) return;

    setPlans((current) =>
      current.map((plan) => (plan.id === saved.id ? saved : plan))
    );
    setPendingToggle(null);
  };

  // Which way the confirmation is pointing. `pendingToggle` is null while it
  // is shut, which reads as Activate — nothing is on screen to see it.
  const deactivating = pendingToggle?.status === "Active";

  return (
    <section className="lg:flex-1 lg:min-h-0 flex flex-col lg:overflow-hidden rounded-md border border-line bg-white">
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6">
        <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
          {/* No count until the list is in: a "(0)" beside a heading that is
              still loading reads as an answer rather than as a blank. */}
          Total plans{status === "ready" ? ` (${plans.length})` : ""}
        </h2>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative w-full sm:w-[260px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
            <input
              type="search"
              value={search}
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder="Search"
              aria-label="Search plans"
              className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
            />
          </div>

          <Button
            size="sm"
            startIcon={<Plus className="w-4 h-4" />}
            onClick={() => setEditing(null)}
            className="flex-shrink-0"
          >
            Add plan
          </Button>
        </div>
      </div>

      {/* Said as soon as the request goes out, whether or not anything is
          drawn for it: this line is not seen, so it costs nothing to be early,
          and the skeleton rows it stands for are hidden from screen readers —
          they are the shape of an answer rather than the answer. */}
      {status === "loading" && (
        <p role="status" className="sr-only">
          Loading your plans…
        </p>
      )}

      <PlansTable
        plans={visible}
        phase={phase}
        empty={emptyState()}
        onEdit={setEditing}
        onToggleStatus={setPendingToggle}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
        resultCount={visible.length}
      />

      <PlanFormModal
        open={editing !== undefined}
        plan={editing}
        suggestions={suggestions.values}
        durations={durations.options}
        saving={saving}
        onClose={() => setEditing(undefined)}
        onSubmit={handleSubmit}
      />

      {/* Both directions are confirmed, not just the one that takes the
          plan down: activating puts a price in front of clients, which is
          not something to do by clipping the wrong row's menu. */}
      <Modal
        open={!!pendingToggle}
        title={deactivating ? "Deactivate plan" : "Activate plan"}
        onClose={() => setPendingToggle(null)}
        className="max-w-[440px]"
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setPendingToggle(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant={deactivating ? "danger" : "primary"}
              size="md"
              onClick={handleConfirmToggle}
              loading={saving}
            >
              {deactivating ? "Deactivate" : "Activate"}
            </Button>
          </>
        }
      >
        <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
          {pendingToggle?.name} ({pendingToggle?.duration}){" "}
          {deactivating
            ? "This plan will be hidden until it is activated again."
            : "This plan will be available after activation."}
        </p>
      </Modal>
    </section>
  );
};

export default BrandPlansTab;
